import React from 'react';
import './deep-dives-styles.css';
import Header from '../components/header';
import GoToTop from '../components/go-to-top';
import components from '../assets/golang-webapp-components.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Articol from '../components/article';

class GoLangWebAppEcr extends React.Component {
    sendEvent = () => {
        if(window.location.hostname === 'localhost' || window.location.hostname.indexOf('192.168.0') === 0) {
            return;
        }

        if(!sessionStorage.getItem('post-golang-webapp-1')) {
            sessionStorage.setItem('post-golang-webapp-1', true);

            fetch('https://api.ipify.org?format=json')
                .then((ipResp) => ipResp.json().then((ipJson) => {
                    fetch('https://09mclyfv7k.execute-api.eu-central-1.amazonaws.com/prod/log_event', {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({type: 'post-ecr-lambda', name: 'binaryfusion.ro', from: ipJson}),
                    }).then((response) => response.json().then((responseData) => {
                        sessionStorage.setItem('success', responseData.EventId);
                    }));
                })
            );
        }
    }

    componentDidMount() {
        if(window.location.hostname !== 'localhost' && window.location.hostname.indexOf('192.168.0') === -1) {
            console.log("Scrolled to top because hostname=" + window.location.hostname);
            window.scrollTo(0,0);
        }

        this.sendEvent();
    }
    render() {
        const codeStyle = {
            'text-shadow': 'none',
            'border': 'none',
            'box-shadow': 'none',
            'padding-left': '30px',
        };

        return (
            <div>
                <Header />
                <title>How to make an AWS Lambda function running Go lang code</title>

                <GoToTop />

                <div className='page'>
                    <h1 className='title'>GoLang Webapp on AWS (Part 1) - ECR and Lambda</h1>
                    <div className='paragraph'>
                        Updated on 31 Aug 2025
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Purpose</h5>
                        The purpose of this series of case studies is to show what issues one can run into when trying to setup such a project and possible solutions. I also expect to improve my own knowledge on these matters by writing these articles.
                        By no means I don't expect that this procedure is the most viable or the most efficient. I am open to any discussion and I am planning to improve my statements here, in the future.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>The goal</h5>
                        The goal of this case study is to use multiple AWS resources to create a template web application to use as a starting point for larger projects. 
                        These are the initial conditions I decided to start with: <br/>
                        - use React for the front end<br/>
                        - use Go lang for the back end<br/>
                        - use AWS for the entire infrastructure<br/>
                        - use a Clean, layered architecture for the backend<br/>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Backend components</h5>
                        Next, I had to choose the precise AWS components to use for the server. Taking into account the pricing, the complexity of the future projects
                        and the userbase, I decided on this:<br/>
                        - I will use multiple <strong>API Gateways</strong> for each topic. Multiple related endpoints will be merged in the same API Gateway<br/>
                        - all API Gateways will be connected to the same <strong>Lambda</strong> function<br/>
                        - the Lambda component will use a <strong>Docker</strong> image running Go lang code<br/>
                        - the Lambda component will be connected to a <strong>DynamoDb</strong>(not much of a choice here)<br/>
                        Some of the resources I used here are: 
                        <a target='_blank' href='https://docs.aws.amazon.com/lambda/latest/dg/go-image.html#go-image-provided' rel="noreferrer"> AWS Developer Guide</a>, 
                        <a target='_blank' href='https://brianmorrison.me/blog/using-go-with-api-gateway/' rel="noreferrer"> Blog post</a>,
                        <a target='_blank' href='https://community.aws/content/2Z4KyWJP5qXDD6StOWJZFXzoRZq/creating-a-lambda-function-with-a-container-based-runtime' rel="noreferrer"> AWS Community post</a>.
                    </div>

                    <div className='paragraph'>
                        This should give a picture of what I was trying to achive. Note that green line are established communication channels and green containers are established components.
                        In the following sections and subsequent parts of this case study, I will tell you the story as I built each of these components.
                    </div>

                    <img alt='architecture components for web app' className='img-row' src={components} />

                    <h5>Lambda function with Go</h5>
                    <div className='paragraph'>
                        <h5 className='title'>Plan</h5>
                        The <strong>Lamda function</strong> on AWS will use a Docker container to process the requests received from the <strong>API Gateway</strong>. The container image
                        needs to be stored also on AWS, in a dedicated <strong>Amazon ECR</strong> image repository. So this is where I started with the plan to:<br/>
                        - Write the Dockerfile<br/>
                        - Build and test locally the image<br/>
                        - Push the image to the remote Amazon ECR<br/>
                        - Create the Lambda function using the above image
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Dockerfile</h5>
                        The first thing I want to deal with is the Docker container that will hold the code for the Lambda function. The code for the Dockerfile is:

                        <SyntaxHighlighter  language='bash' style={dark} customStyle={codeStyle}>
                    {`FROM golang:1.25.0 AS build
WORKDIR /build_dir
# Copy dependencies list
COPY go.mod go.sum ./
# Build with optional lambda.norpc tag
COPY . .
RUN go build -tags lambda.norpc -o main main.go
# Copy artifacts to a clean image
FROM public.ecr.aws/lambda/provided:al2023
COPY --from=build /build_dir/main ./main

EXPOSE 8080

ENTRYPOINT [ "./main" ] `}
                        </SyntaxHighlighter  >

                        This will copy all the code from the local directory, inside the Docker container and run the below command, essentially turning my container into a server for the requests comming from API Gateway.
                        This requires that <inline-code>main.go</inline-code> contains the code for processing the <inline-code>events.APIGatewayProxyRequest</inline-code> event objects.
                        <div className='code'>
                            go build -tags lambda.norpc -o main main.go
                        </div>
                        This is the build command for the go lang code. The <inline-code>lambda.norpc</inline-code> tag is necessary to be able to use the API Gateway event object.
                        Adding tags on the build command is a common practice for Go projects that need to be built for different environments, such as a local machine versus an AWS Lambda function.
                        The tags will tell the go compiler which files to include or exclude from the build.
                    </div>

                    
                    <div className='paragraph'>
                        <h5 className='title'>Local testing Lambda function</h5>
                        The procedure for updating the code of the Lambda function for me it was like this:<br/>
                        - Update code locally<br/>
                        - Update docker image locally<br/>
                        - Update docker registry on ECR<br/>
                        - Update Lambda function with the new image<br/>
                    </div>


                    <div className='paragraph'>
                        To build and run localy the the image, I used
                        <div className='code'>
                            {`docker build --platform linux/amd64 -t docker-image:test .
docker run -d -p 9000:8080 --entrypoint /usr/local/bin/aws-lambda-rie docker-image:test ./main`}
                        </div>
                        The <inline-code>--platform</inline-code> option is necessary to be able to run the image in the AWS environment.
                        This might show in Docker Desktop a warning that the image is not optimized for your system by this is ok.
                        More details how you can create the Dockerfile, build the image and run it can be found <a target='_blank' href='https://docs.aws.amazon.com/lambda/latest/dg/go-image.html#go-image-clients' rel="noreferrer">here</a>.
                        Of course, the name and the tag <inline-code>docker-image:test</inline-code> should be replaced with whatever is relevant to you.
                        Now, the image should run locally, so that in theory I should be able to send some requests to it and receive the expected response.
                        To make a call to the future Lambda function, I used this command:
                        <div className='code'>
                            {`curl -v -X POST --data '{"body": "{\\"param1\\": \\"A\\", \\"param2\\": \\"B\\", \\"source\\":\\"A\\"}"}' http://localhost:9000/2015-03-31/functions/function/invocations`}
                        </div>
                        Here, I make a <inline-code>POST</inline-code> call, with a json payload having the values for <inline-code>firstName lastName source</inline-code>.

                        <div className='paragraph'>
                            Only the path property will be checked to match a specific HTTP request. To be able to call a specific handler function add into the payload the <inline-code>path</inline-code> property, like this:
                            <div className='code'>
                                {`curl -v -X POST --data '{"body": "{\\"param1\\": \\"A\\", \\"param2\\": \\"B\\", \\"source\\":\\"A\\"}, \n\t "path": "/robots" "}' http://localhost:9000/2015-03-31/functions/function/invocations`}
                            </div>
                        </div>
                    </div>

                    <div className='side-story'>
                        <h2>Side story: Docker Desktop</h2>

                        <div className='paragraph'>
                            I noticed that it was very helpful for me to use in this development stage <a href='https://www.docker.com/products/docker-desktop/' target='_blank' rel='noreferrer'>Docker Desktop</a> app. I tested it on Mac and Ubuntu and I was easily
                            able to view which container is running, operate on it, and most importantly view the logs in real time.
                        </div>
                    </div>


                    <div className='paragraph'>
                        More like a hack I used a technique to test functionality under development without starting a Docker container and actually have the HTTP server running and than sending the requests. I used the <inline-code>testing</inline-code> package, that comes toghether with the standard Go lang SDK and used the <inline-code>handler</inline-code> function in <inline-code>main.go</inline-code> to process specific requests and see how my code behaves.
                    </div>

                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`package main

import (
	"strings"
	"testing"

	"github.com/aws/aws-lambda-go/events"
)

func TestHandlerFunction_for_prod_get_robots(t *testing.T) {
	request := events.APIGatewayProxyRequest{}
	request.Path = "https://xxxx.execute-api.eu-central-1.amazonaws.com/prod/get_robots"
	request.Body = '{"source": "B", "firstName": "abc", "lastName": "def"}'
	_, err := handler(request)

	if err != nil {
		t.Fatal("Should not get an error")
	}
}

func TestHandlerFunction_for_prod_create_robot(t *testing.T) {
	request := events.APIGatewayProxyRequest{}
	request.Path = "https://xxxx.execute-api.eu-central-1.amazonaws.com/prod/create_robot"
	request.Body = '{"source": "B", "name": "abc"}'
	resp, err := handler(request)

	if err != nil {
		t.Fatal("Should not get an error")
	}

	if resp.Headers["Access-Control-Allow-Origin"] != "https://master.xxxx.amplifyapp.com" {
		t.Fatal("Incorrect Allow-Origin header")
	}

	if !strings.Contains(resp.Body, "robot with name abc1") {
		t.Fatalf("Incorrect response: %s", resp.Body)
	}
}`}
                        </SyntaxHighlighter>


                    <div className='paragraph'>
                        <h5 className='title'>Receiving requests</h5>
                        The HTTP request that will be sent from the React front-end will be processed by a single <strong>Lambda function</strong>. I decided to go with this approach but there are multple posibilities here. The consequence of having one Lambda function handling all requests is that I will have a single, larger code base and only one docker image. Using a Lambda function oposed to a VPS means that in any case a Docker container will spawn for every received request. This will lead to a small delay the first time an event
                        will be received after a period of inactivity. In my <inline-code>main</inline-code> function, the <inline-code>handler()</inline-code> function is checking the request path to choose which handler function to send for processing.
                    </div>

                        <SyntaxHighlighter  language='go' style={dark} customStyle={codeStyle}>
                            {`func main() {
	lambda.Start(handler)
}

func handler(request events.APIGatewayProxyRequest) (events.APIGatewayProxyResponse, error) {
	matched, err := regexp.Match('.*/robots$., []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleGetRobotsRequest(request)
	}

	matched, err = regexp.Match(..*/get_robots$., []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleGetRobotsRequest(request)
	}

	matched, err = regexp.Match(..*/create_robot$., []byte(request.Path))
	if err == nil && matched {
		return internal.GetLambdaGateway().HandleCreateRobotRequest(request)
	}

	return internal.GetLambdaGateway().GetInvalidRequestResponse(request), errors.New("path not defined. Request path is " + request.Path)
}`}
                        </SyntaxHighlighter>

                    <div className='paragraph'>
                        The matched string for <inline-code>/robots</inline-code>,<inline-code>/get_robots</inline-code>or<inline-code>/create_robot</inline-code>will
                        correspond to the <incline-code>path</incline-code> property of the <inline-code>events.APIGatewayProxyRequest</inline-code> object as mentioned
                        above. The url <inline-code>http://localhost:9000/2015-03-31/functions/function/invocations</inline-code> should not match any of these strings. Only
                        the property path should. This will be passed from API Gateway in the AWS environment.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Proxy integration</h5>

                        Since I am using <inline-code>request.Path</inline-code> from the <inline-code>events.APIGatewayProxyResponse</inline-code> object an important point to note here is that this property will only be available to us here, in the Lambda function code only if we activate <strong>Lambda proxy integration</strong> when we create a resource in the <strong>API Gateway</strong> service.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Amazon ECR</h5>
                        I will skip all the necessary software requirements like <inline-code>docker</inline-code> or <inline-code>aws</inline-code> and possibly some other tools. I will not describe those here since I want to focus on the things harder to find on other posts.
                        To be able to create a Lambda function that uses a Docker image, that image should be located in a repository. That's why, first you need to have a private repository in place, in the <strong>Amazon Elastic Container Registry</strong> or <strong>ECR</strong>. To create one 
                        takes nothing more than to use the "Create repository" button in the web UI. Keep in mind that the selected geographic location matters both for traffic speed and for future 
                        configurations in the sense that you will need to select the same geographic location to be able to find your image.
                    </div>

                    <div className='paragraph'>
                        The next thing you need is to authenticate <inline-code>docker</inline-code> to an Amazon ECR registry. This can be done in multiple ways as described <a href='https://docs.aws.amazon.com/AmazonECR/latest/userguide/registry_auth.html' target='_blank' rel='noreferrer'>here</a>. I used the method with the <strong>authorization token</strong>.
                        For your reference the exact command I used is
                        <div className='code'>
                            {`aws ecr get-login-password --region eu-central-1 | docker login --username AWS --password-stdin xxxxxxxxxxx.dkr.ecr.eu-central-1.amazonaws.com`}
                        </div>
                        The URI of the repository in the command, the one containing the 'x's, can be obtained from the list of repositories in the <strong>ECR</strong> UI.
                        Here, <inline-code>get-login-password</inline-code> command could throw this error:
                    </div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> Error saving credentials</div>
                        The complete error looks like this:<br/> <span className='error-red'>Error saving credentials: error storing credentials - err: exit status 1, out: `error storing credentials - err: exit status 1, out: `pass not initialized: exit status 1: Error: password store is empty. Try "pass init".`</span><br/><br/>
                        This error can be simply solved by editting the <inline-code>~/.docker/config.json</inline-code> file. In my case the only thing I did was to remove the entire line containing "credStore". I am using an Ubuntu operating system but I understand that this solution is similar on other systems with regard to different location of the file.
                    </div>

                    <div className='space20'></div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> AccessDeniedException</div>
                        The complete error looks like this:<br/><span className='error-red'>An error occurred (AccessDeniedException) when calling the GetAuthorizationToken operation: User: arn:aws:iam::xxxxxxxxx671:user/amplify-user is not authorized to perform: ecr:GetAuthorizationToken on resource: * because no identity-based policy allows the ecr:GetAuthorizationToken action
                        Error: Cannot perform an interactive login from a non TTY device</span><br/><br/>
                        The token obtained by the <inline-code>get-login-password</inline-code> is temporary. This error it's a sign that you should re-authenticate the Docker client. Simply running again the command above should solve this issue.
                    </div>

                    <div className='paragraph'>
                        Once Docker is authenticated to your Amazon ECR registry, you can push the image with
                        <div className='code'>
                            {`docker tag docker-image:test xxxxx.dkr.ecr.eu-central-1.amazonaws.com/xxxxx
docker push xxxxx.dkr.ecr.eu-central-1.amazonaws.com/xxxxx`}
                        </div>
                        These should be adjusted by using the correct image and tag names and the correct repository URI.
                    </div>

                    <div className='paragraph'>
                        Here another possible error could be encountered: 
                    </div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> No identity-based policy</div>
                        The complete error looks like this:<br/><span className='error-red'>An error occurred (AccessDeniedException) when calling the GetAuthorizationToken operation: User: arn:aws:iam::590183958671:user/amplify-user is not authorized to perform: ecr:GetAuthorizationToken on resource: * because no identity-based policy allows the ecr:GetAuthorizationToken action
                        Error: Cannot perform an interactive login from a non TTY device</span><br/><br/>
                        The issue here is that the user mentioned in the error message, here is <inline-code>amplify-user</inline-code> does not have enough permissions to be able to access the repository and push the image. To solve this error I added to new permissions to the specified user: 
                        <inline-code>AWSAppRunnerServicePolicyForECRAccess</inline-code> and <inline-code>EC2InstanceProfileForImageBuilderECRContainerBuilds</inline-code>. You can do this in AWS platform using <strong>IAM</strong>, 
                        look for the exact user, in the Users page under <strong>Permissions</strong> tab use <strong>Add permissions</strong> button and choose <strong>Attach policies directly</strong>.
                    </div>

                    <div className='paragraph'>
                        Finally, after this step, I was able to push the image to my private repository in ECR and confirm in the UI that the operation was succesfull. Having this step complete I could move on the next piece: the API.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Pushing commands</h5>
                        For your reference, on the ECR page, after selecting your repository, by pressing the <strong>View push commands</strong> button, you can find the exact
                        commands to use for authentication, tagging and pushing the image. These commands are specific to your repository and your region.
                    </div>

                    <div className='side-story'>
                        <h2>Side story: Pushing with Cloud9</h2>

                        <div className='paragraph'>
                           As a trick, you can push the image to the repository by using <strong>Cloud9</strong> evironment, provided also by AWS. Cloud9 is an cloud IDE that lets you all sort of operations without using your local, on-device tools.
                           In this case I noticed that I can write the source code for the image, in the repository and push it to <strong>Code Commit</strong>. This operation can be done locally on directly in Cloud9 IDE. You can 
                           continue to use this online tool to build and push the image to ECR since it uses a different user with enough permissions to get to operation done.
                        </div>
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Live Lambda function</h5>
                        Everything so far was necessary to be able to create the Lambda function that will process the requests from the API. In <strong>AWS Lambda dashboard</strong> use the "Create function" button 
                        and choose, obviously <strong>Container image</strong> option. Again, the region is important to be the same as the one selected in ECR. When using "Browse images" button, we should be able to
                        find our private repository and then the image.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Testing Lambda function</h5>
                        To be able to test the handler function in the AWS Console, you need to create a test event.
                        The event should have in the json payload the <inline-code>path</inline-code> property with a value such <inline-code>/robots</inline-code>
                    </div>

                    <div className='space20'></div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> Error - failed to push</div>
                        If getting an error while trying to push to Git Commit for reasons of missing access, running <inline-code>aws configure</inline-code> will solve the issue. Since I used HTTPS method for connecting to the repository this is the resource you need: <a target='_blank' rel="noreferrer" href='https://docs.aws.amazon.com/codecommit/latest/userguide/setting-up-https-unixes.html'>Setup HTTPS connection</a>
                    </div>

                    <div className='space30'></div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> Unable to clone repository</div>
                        If this error comes up when trying to clone the repository instead, with the more verbose error text:<br/>
                        <span className='error-red'>fatal: unable to access 'https://git-codecommit.eu-central-1.amazonaws.com/v1/repos/xxxx/': The requested URL returned error: 403 2024-05-14T08:05:47.507Z [ERROR]: !!! CustomerError: Unable to clone repository due to user error code: 128</span>,<br/>
                        the solution is to add the <inline-code>AmplifyConsoleServiceRole-AmplifyRole</inline-code> permission to the dev user.
                    </div>

                    <h5>Next</h5>
                    <div className='paragraph'>
                        In the next article I will show how I build the front-end, the connection to the Lambda function and the problems I ran into during this process.
                    </div>

                    <div className='articles mt-5'>
                        <Articol title='Hosted React to AWS Amplify' to={'/dp-dv-react-to-aws'} />
                        <Articol title='GoLang Web app on AWS (Part 2) - Front-end and CORS' to={'/dp-golang-webapp-cors'} />
                    </div>

                    <div className='mb-10'></div>
                </div>
            </div>

        );
    }

}

export default GoLangWebAppEcr;